import * as Yup from 'yup';
import date from 'date-and-time';
import OptionalImageLabel from './optional-image-label.js';

export const BALANCE_CAP = 'balanceCap';

const leadingZero = str => `0${str}`.slice(-2);

export const createStreamFields = (tr = {}) => [
	{
		id: 'name',
		label: tr.WE_SPIN_DASHBOARD_STREAM_FIELD_NAME_LABEL,
		yup: Yup.string()
			.trim()
			.max(50, tr.WE_SPIN_DASHBOARD_STREAM_FIELD_NAME_MAX_VALIDATION_MSG)
			.required(tr.WE_SPIN_DASHBOARD_STREAM_FIELD_NAME_REQ_VALIDATION_MSG)
	},
	{
		id: 'description',
		label: tr.WE_SPIN_DASHBOARD_STREAM_FIELD_DESCR_LABEL,
		type: 'textarea',
		yup: Yup.string()
			.trim()
			.max(250, tr.WE_SPIN_DASHBOARD_STREAM_FIELD_DESCR_MAX_VALIDATION_MSG)
			.required(tr.WE_SPIN_DASHBOARD_STREAM_FIELD_DESCR_REQ_VALIDATION_MSG)
	},
	{
		groupTitle: tr.WE_SPIN_DASHBOARD_STREAM_FIELD_GROUP_WHEN
	},
	{
		id: 'startingDate',
		label: tr.WE_SPIN_DASHBOARD_STREAM_FIELD_START_DATE_LABEL,
		type: 'date',
		defaultValue: () => {
			return date.format(new Date(), 'YYYY-MM-DD');
		},
		yup: Yup.string().trim().required(tr.WE_SPIN_DASHBOARD_STREAM_FIELD_START_DATE_REQ_VALIDATION_MSG)
	},
	{
		id: 'startingTime',
		label: tr.WE_SPIN_DASHBOARD_STREAM_FIELD_START_TIME_LABEL,
		type: 'time',
		defaultValue: () => {
			const oneHourFromCurrentTime = new Date(new Date().getTime() + 3600000);
			return `${leadingZero(oneHourFromCurrentTime.getHours())}:${leadingZero(
				oneHourFromCurrentTime.getMinutes()
			)}`;
		},
		yup: Yup.string().required(tr.WE_SPIN_DASHBOARD_STREAM_FIELD_START_TIME_REQ_VALIDATION_MSG)
	},
	{
		id: 'durationInMinutes',
		label: tr.WE_SPIN_DASHBOARD_STREAM_FIELD_LENGTH_LABEL,
		type: 'number',
		defaultValue: 60, // has to be a number
		yup: Yup.number()
			.min(1, tr.WE_SPIN_DASHBOARD_STREAM_FIELD_LENGTH_MIN_VALIDATION_MSG)
			.required(tr.WE_SPIN_DASHBOARD_STREAM_FIELD_LENGTH_REQ_VALIDATION_MSG)
	},
	{
		id: BALANCE_CAP,
		label: tr.WE_SPIN_DASHBOARD_STREAM_FIELD_BALANCE_CAP_LABEL,
		type: 'number',
		defaultValue: 0, // has to be a number
		yup: Yup.number()
			.min(1, tr.WE_SPIN_DASHBOARD_STREAM_FIELD_BALANCE_CAP_MIN_VALIDATION_MSG)
			.required(tr.WE_SPIN_DASHBOARD_STREAM_FIELD_BALANCE_CAP_REQ_VALIDATION_MSG)
	},
	{
		id: 'language',
		label: tr.WE_SPIN_DASHBOARD_STREAM_FIELD_LANGUAGE_LABEL,
		skip: true,
		yup: Yup.string()
			.trim()
			.test('Unknown language', item => {
				return ['sv', 'en', 'no', 'es', 'fi', 'da', 'dk'].find(element => item === element);
			})
			.max(2, tr.WE_SPIN_DASHBOARD_STREAM_FIELD_LANGUAGE_MAX_VALIDATION_MSG)
			.required(tr.WE_SPIN_DASHBOARD_STREAM_FIELD_LANGUAGE_REQ_VALIDATION_MSG)
	},
	{
		id: 'eventTag',
		label: tr.WE_SPIN_DASHBOARD_STREAM_FIELD_EVENT_TAG_LABEL,
		defaultValue: '',
		skip: true
	},
	{
		id: 'streamBgUrl',
		label: tr.WE_SPIN_DASHBOARD_STREAM_FIELD_IMAGE_LABEL,
		type: 'file',
		skip: true
	}
];

export const createPrivateStreamFields = (tr = {}, isStreamer = true, buyInLimit, editMode = false) =>
	[
		// Streamer initial info
		!isStreamer && {
			id: 'streamerName',
			label: tr.WE_SPIN_DASHBOARD_EDIT_PROFILE_STREAMER_NAME,
			yup: Yup.string()
				.trim()
				.max(50, tr.WE_SPIN_DASHBOARD_EDIT_PROFILE_STREAMER_NAME_MAX_VALIDATION_MSG)
				.required(tr.WE_SPIN_DASHBOARD_EDIT_PROFILE_STREAMER_NAME_REQ_VALIDATION_MSG)
		},
		{
			id: 'name',
			label: tr.WE_SPIN_DASHBOARD_STREAM_FIELD_EVENT_NAME,
			yup: Yup.string()
				.trim()
				.max(50, tr.WE_SPIN_DASHBOARD_STREAM_FIELD_NAME_MAX_VALIDATION_MSG)
				.required(tr.WE_SPIN_DASHBOARD_STREAM_FIELD_NAME_REQ_VALIDATION_MSG)
		},
		{
			id: 'description',
			label: tr.WE_SPIN_DASHBOARD_STREAM_FIELD_DESCR_LABEL,
			type: 'textarea',
			yup: Yup.string()
				.trim()
				.max(250, tr.WE_SPIN_DASHBOARD_STREAM_FIELD_DESCR_MAX_VALIDATION_MSG)
				.required(tr.WE_SPIN_DASHBOARD_STREAM_FIELD_DESCR_REQ_VALIDATION_MSG)
		},
		/* STO-210
		{
			id: 'streamUrl',
			label: tr.WE_SPIN_DASHBOARD_STREAM_FIELD_URL_LABEL,
			yup: Yup.string()
				.trim()
				.max(
					100,
					tr.WE_SPIN_DASHBOARD_STREAM_FIELD_URL_MAX_VALIDATION_MSG
				)
				.required(
					tr.WE_SPIN_DASHBOARD_STREAM_FIELD_URL_REQ_VALIDATION_MSG
				)
		},
		*/
		!isStreamer && {
			groupTitle: tr.WE_SPIN_DASHBOARD_STREAM_FIELD_GROUP_OPTIONAL
		},
		!isStreamer && {
			id: 'streamerImageUrl',
			label: <OptionalImageLabel />,
			labelProps: {
				onClick: event => {
					event.preventDefault();
				}
			},
			inputProps: {
				accept: 'image/*'
			},
			type: 'file',
			className: 'u-relative u-oh upload-file',
			helperText: tr.WE_SPIN_DASHBOARD_STREAM_FIELD_STREAMER_IMAGE_DESC
		},
		isStreamer && {
			id: 'startingDate',
			label: tr.WE_SPIN_DASHBOARD_STREAM_FIELD_START_DATE_LABEL,
			type: 'date',
			defaultValue: () => {
				return date.format(new Date(), 'YYYY-MM-DD');
			},
			yup: Yup.string().trim().required(tr.WE_SPIN_DASHBOARD_STREAM_FIELD_START_DATE_REQ_VALIDATION_MSG)
		},
		isStreamer && {
			id: 'startingTime',
			label: tr.WE_SPIN_DASHBOARD_STREAM_FIELD_START_TIME_LABEL,
			type: 'time',
			defaultValue: () => {
				const oneHourFromCurrentTime = new Date(new Date().getTime() + 3600000);
				return `${leadingZero(oneHourFromCurrentTime.getHours())}:${leadingZero(
					oneHourFromCurrentTime.getMinutes()
				)}`;
			},
			yup: Yup.string().required(tr.WE_SPIN_DASHBOARD_STREAM_FIELD_START_TIME_REQ_VALIDATION_MSG)
		},
		!!buyInLimit &&
			!editMode && {
				groupTitle: tr.WE_SPIN_DASHBOARD_STREAM_FIELD_GROUP_BUY_IN
			},
		!!buyInLimit &&
			!editMode && {
				id: 'buyIn',
				label: tr.WE_SPIN_DASHBOARD_STREAM_FIELD_EVENT_OPTIONAL_BUY_IN,
				yup: Yup.number()
					.typeError(tr.WE_SPIN_DASHBOARD_STREAM_FIELD_BUY_IN_NUMBER_VALIDATION_MSG)
					.max(buyInLimit)
					.nullable(true)
			}
	].filter(f => !!f);

export const createEditStreamFields = createStreamFields;

export const createEditPrivateStreamFields = createPrivateStreamFields;
